import { useEffect, useState, useMemo, useCallback } from 'react';
import type { UniverseStreamChannel } from '@/services/stream/types';
import { canCallWhatsApp, parseCallExpiry } from '@/services/calls/callPermission';

export function useChannelData(channelProp?: UniverseStreamChannel | null) {
    const channel = channelProp;

    // Initial data setup - memoize to avoid recreating on every render
    const getInitialData = useCallback(() => {
        if (!channel?.data) {
            return {
                name: 'Chat',
                image: undefined,
                clientPhone: '',
                clientUrl: '',
                canDelete: false,
                status: '',
                canCallWhatsApp: false,
                callStatus: '',
                callIsPermanent: false,
                callExpiresAt: null as number | null,
            };
        }

        return {
            name: channel.data.name ?? 'Chat',
            image: channel.data.image,
            clientPhone: channel.data.custom_data?.client_phone ?? '',
            clientUrl: channel.data.custom_data?.client_url ?? '',
            canDelete: channel.data.custom_data?.can_delete ?? false,
            status: channel.data.custom_data?.status ?? 'inactive',
            canCallWhatsApp: canCallWhatsApp(channel.data.custom_data),
            callStatus: channel.data.custom_data?.call_status ?? '',
            callIsPermanent: channel.data.custom_data?.call_is_permanent ?? false,
            callExpiresAt: parseCallExpiry(channel.data.custom_data?.call_expired_at),
        };
    }, [channel]);

    const [data, setData] = useState(getInitialData);

    useEffect(() => {
        if (!channel) return;

        // Ensure we have current data when channel changes
        setData(getInitialData());

        const handleUpdate = (event: any) => {
            // Only update if the event actually changed channel data
            // message.new events don't modify channel.data, so skip them
            if (event.type === 'message.new') {
                // Check if the message actually updated channel custom_data
                if (!event.channel_id || !event.message?.custom_data) {
                    return;
                }
            }

            const newData = getInitialData();
            setData(prevData => {
                // Only update state if data actually changed
                if (JSON.stringify(prevData) === JSON.stringify(newData)) {
                    return prevData;
                }
                return newData;
            });
        };

        // Listen to events that can modify channel data
        const events = ['channel.updated', 'message.new', 'channel.truncated', 'member.updated'] as const;
        const subscriptions = events.map(event => channel.on(event, handleUpdate));

        return () => {
            subscriptions.forEach(s => s.unsubscribe());
        };
    }, [channel, getInitialData]);

    return data;
}
